import { Language } from "@/data/model/Enums";
import type { IMultiLanguageText } from "@/data/model/Shared";
import { Misc } from "@/data/util/Misc";

import { TastierGlobal } from "@/portal/globalAccess";

export class MultiLanguageInput {
    private $element: JQuery;
    private $input: JQuery;
    private $flags: JQuery;

    private values: any[] = [];
    private activeLang?: Language;

    public onChange?: { (m: IMultiLanguageText) };

    constructor($element?: JQuery) {
        this.$element = $('<div class="multilanginput" />');

        if ($element === undefined) {
            this.$input = $('<textarea class="form-control" />').appendTo(this.$element);
        } else {
            this.$element.insertAfter($element);
            this.$input = $element.detach().appendTo(this.$element);
        }

        this.$input.change(() => {
            this.values[this.activeLang!] = this.$input.val().length === 0 ? undefined : this.$input.val();

            if (this.onChange !== undefined) this.onChange(this.getValues());
        });

        this.$flags = $('<div class="flags" />').appendTo(this.$element);

        Misc.eachEnum(Language, (i, e) => {
            $('<div class="flag ' + e.toLowerCase() + '" title="' + e + '"></div>')
                .data("model", i)
                .appendTo(this.$flags)
                .click((e) => {
                    this.changeToLanguage($(e.currentTarget).data("model"));
                });
        });

        if (TastierGlobal.getLang() === undefined) this.changeToLanguage(Language.German);
        else this.changeToLanguage(TastierGlobal.getLang());
    }

    public getValue(language: Language): string {
        return this.values[language];
    }

    public setValue(value: string | undefined, language: Language): MultiLanguageInput {
        this.values[language] = value;
        if (language === this.activeLang) this.$input.val(value!);

        return this;
    }

    public setValues(val?: IMultiLanguageText): MultiLanguageInput {
        this.setValue(val === undefined ? undefined : val.de, Language.German);
        this.setValue(val === undefined ? undefined : val.en, Language.English);
        this.setValue(val === undefined ? undefined : val.fr, Language.French);
        this.setValue(val === undefined ? undefined : val.it, Language.Italian);

        return this;
    }

    public getValues(): IMultiLanguageText {
        return {
            de: this.getValue(Language.German),
            en: this.getValue(Language.English),
            fr: this.getValue(Language.French),
            it: this.getValue(Language.Italian),
        };
    }

    public appendText(text: string): MultiLanguageInput {
        this.setValue((this.getValue(this.activeLang!) ?? "") + text, this.activeLang!);
        return this;
    }

    public setError(hasError: boolean): MultiLanguageInput {
        if (hasError) this.$input.addClass("is-invalid");
        else this.$input.removeClass("is-invalid");

        return this;
    }

    // the flags stay clickable so the other translations can still be read
    public setDisabled(isDisabled: boolean): MultiLanguageInput {
        this.$input.prop("disabled", isDisabled);

        return this;
    }

    public hide() {
        this.$element.hide();
    }

    public show() {
        this.$element.show();
    }

    public renderTo($element: JQuery): MultiLanguageInput {
        this.$element.appendTo($element);
        return this;
    }

    private changeToLanguage(lang: Language) {
        if (this.activeLang === lang) return;

        this.activeLang = lang;
        const newVal = this.values[lang];
        this.$flags.find("> .active").removeClass("active");
        this.$flags.children().each((i, e) => {
            const el = $(e);
            if (el.data("model") === lang) {
                el.addClass("active");
                return false;
            }
        });

        this.$input.val(newVal);
    }

    public static fromInput($element: JQuery): MultiLanguageInput {
        return new MultiLanguageInput($element);
    }
}
